import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';

import { selectUser } from 'store';
import { flowNeedsProgramEligibility, getFlowByUrl } from 'pages/Login/login.settings';
import { isSignupInProgress } from 'utils/onboarding';

import Loader from 'shared/Loader';

import { isBootstrapQuerySettled, useAuthBootstrapStatus } from 'contexts/authBootstrapContext';
import { FLOW_TYPES } from 'constants/onboarding';
import { useAppInstallPrompts } from 'hooks/useAppInstallPrompts/useAppInstallPrompts';
import { useAppSelector, useQuery } from 'hooks';
import { useMyPrograms } from 'hooks/useMyPrograms/useMyPrograms';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { hasExitedFlow } from 'utils/flowExit';
import { FeatureFlag, FlowTypes, PlanTypes } from 'utils/enums';
import { PathName } from './paths';

const Router = () => {
  const prevLocation = useLocation();
  const user = useAppSelector(selectUser);
  const { accessToken } = user;
  const { hadOrHasWeightManagement } = useMyPrograms();
  const { myPrograms } = useAuthBootstrapStatus();
  const query = useQuery();
  const isSignUp = useRouteMatch(PathName.SignUp);
  const isVerifyEmail = !!useRouteMatch(PathName.VerifyEmail);
  const isVerifyIdentity = !!useRouteMatch(PathName.VerifyIdentity);
  const isVerifyYourIdentity = !!useRouteMatch(PathName.VerifyYourIdentity);

  const unprotectedRoutes = [
    PathName.Login,
    PathName.SignUp,
    PathName.ForgotPassword,
    PathName.CreateNewPassword,
    PathName.CreateFreeAccount,
    PathName.VerifyEmail,
    PathName.VerifyIdentity,
    PathName.ConfirmAccount,
    PathName.CreateAppointment,
    PathName.Join
  ];

  const isNotProtectedRoute =
    unprotectedRoutes.some((route) => useRouteMatch(route)) &&
    window.location.pathname !== PathName.Home;

  // Mirror the two early-return conditions below so the app-install-prompt effect can be called
  // unconditionally (required by the Rules of Hooks) while still matching, byte for byte, when
  // the pre-effect `!isSignUp && appendAppBanner()` call used to run: never on a render that is
  // about to redirect away.
  const willRedirectUnauthenticated = !isNotProtectedRoute && !accessToken;
  const willRedirectAuthenticated =
    isNotProtectedRoute &&
    !!accessToken &&
    !query.get('login-token') &&
    !query.get('action') &&
    !isVerifyEmail &&
    !isVerifyIdentity;

  useAppInstallPrompts(!isSignUp && !willRedirectUnauthenticated && !willRedirectAuthenticated);

  const navigateTo = (to: string) => {
    const queryString = query.toString();
    if (queryString.length > 0) {
      to += (to.includes('?') ? '&' : '?') + queryString;
    }
    return <Navigate to={to} replace />;
  };

  /*
   * `hasExitedFlow()` (LMD-2715): the redirect below reads only the CURRENT pathname, so it turns a
   * patient back from Home identically whether they clicked there or loaded the URL fresh — a full
   * document navigation is not an escape from it. An explicit exit is the only thing that can be,
   * and it is scoped to the patient's own action and to this session.
   */
  if (
    accessToken &&
    isSignupInProgress(user) &&
    !hasExitedFlow() &&
    !isVerifyEmail &&
    !isVerifyIdentity &&
    !isVerifyYourIdentity
  ) {
    const flowUrl = user.flowUrl ?? '';
    const unconditional = getFlowByUrl(flowUrl);
    const flowPathname = typeof unconditional === 'string' ? unconditional : unconditional.pathname;

    /*
     * A patient already on the route their flow names has nothing left for this to decide, and the
     * deferral below would otherwise blank the flow they are part-way through while a refetch is in
     * flight. Their eligibility is still enforced — by that route's own guard, which is the more
     * specific authority and moves them if it has to.
     */
    if (prevLocation.pathname !== flowPathname) {
      /*
       * The programs slice is empty both before `/my-programs` resolves and when the patient has no
       * weight-management program, so answering mid-flight reads "no program" for a patient who has
       * one and sends them into a migration flow that then starts its own initialisation requests.
       * If that fetch never arrives they stay in it. Waiting is the same deferral `RequireProgram`
       * uses, reading the same argless bootstrap query; settled includes failed, so a fetch that
       * does not arrive falls through rather than holding the route open.
       */
      if (flowNeedsProgramEligibility(flowUrl) && !isBootstrapQuerySettled(myPrograms)) {
        return <Loader isVisible />;
      }

      // This fires on every route a signup-in-progress patient lands on, so the destination has to
      // be one that will accept them — otherwise the destination's own guard sends them back here
      // and the two redirect at each other. Both halves read the same program fact for that reason.
      const destination = getFlowByUrl(flowUrl, { hadOrHasWeightManagement });
      const pathname = typeof destination === 'string' ? destination : destination.pathname;
      if (prevLocation.pathname !== pathname) {
        return <Navigate to={destination} replace />;
      }
    }
  }

  if (willRedirectUnauthenticated) {
    const redirectTo =
      prevLocation.pathname !== PathName.Home
        ? `${PathName.Login}?redirectTo=${prevLocation.pathname}`
        : PathName.Login;
    return navigateTo(redirectTo);
  }

  if (isNotProtectedRoute && accessToken && !query.get('login-token') && !query.get('action')) {
    const redirectTo = query.get('redirectTo') || PathName.Home;
    query.delete('redirectTo');
    query.delete('token');
    if (!isVerifyEmail && !isVerifyIdentity) return navigateTo(redirectTo);
  }

  return <Outlet />;
};

export default Router;

export const SignupRouter = () => {
  const { accessToken } = useAppSelector(selectUser);
  const { search } = useLocation();
  const { flow = FlowTypes.WeightManagementFlow, plan = PlanTypes.WeightManagement } = useParams();
  let patchedFlow = FLOW_TYPES.hasOwnProperty(flow) ? flow : FlowTypes.WeightManagementFlow;

  const isDisabledInternalSignups = useFlag(FeatureFlag.DisableInternalSignups);

  const redirectPath = accessToken
    ? PathName.Login
    : !FLOW_TYPES.hasOwnProperty(flow) || isDisabledInternalSignups
      ? PathName.CreateAppointment
      : `${PathName.SignUp}/${plan}/${patchedFlow}/${FLOW_TYPES[patchedFlow as FlowTypes][0] + search}`;

  return <Navigate to={redirectPath} replace />;
};
